import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Play, UploadCloud, FolderCheck, BellRing, Clapperboard } from "lucide-react";
import { siteVideo } from "../../lib/api";
import { tr } from "../../lib/i18n";
import { Eyebrow, fade } from "./Sections";

const API = process.env.REACT_APP_BACKEND_URL;

const FRAMES = [
  { Icon: UploadCloud, label: "Drop it in" },
  { Icon: FolderCheck, label: "Filed & named" },
  { Icon: BellRing, label: "Reminded in time" },
];

// Click-to-play facade: nothing third-party loads until the visitor presses play (keeps the zero-tracking promise).
const imgSrc = (p) => (/^(blob:|data:|https?:)/.test(p) ? p : `${API}${p}`);

// compact = force the phone layout (Admin phone preview renders at desktop viewport width).
export const Facade = ({ onPlay, title, poster, testId = "video-tour-play", compact = false }) => {
  const m = (mobile, desk) => (compact ? mobile : `${mobile} ${desk}`);
  return (
    <button type="button" onClick={onPlay} data-testid={testId} aria-label={tr("Play the tour video")} className="group relative w-full aspect-video rounded-3xl overflow-hidden bg-foreground text-background text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      {poster ? <img src={imgSrc(poster)} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,hsl(var(--primary)/0.45),transparent_55%),radial-gradient(ellipse_at_80%_90%,hsl(var(--primary)/0.25),transparent_50%)]" />}
      {poster && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />}
      <div className="absolute inset-0 opacity-[0.08] [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22120%22 height=%22120%22 filter=%22url(%23n)%22/></svg>')]" />
      <div className={`absolute right-6 ${m("left-5 bottom-4", "sm:left-10 sm:bottom-10")}`}>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono opacity-70">{tr("60-second tour")}</div>
        <div className={`font-serif mt-1 max-w-md leading-snug ${m("text-lg", "sm:text-3xl")}`}>{title || tr("Watch a family's papers go from shoebox to sorted.")}</div>
      </div>
      <span className={`absolute inset-0 grid place-items-center ${m("-translate-y-7", "sm:translate-y-0")}`}>
        <span className={`rounded-full bg-background text-foreground grid place-items-center shadow-xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95 ${m("h-11 w-11", "sm:h-20 sm:w-20")}`}><Play className={`ml-1 fill-current ${m("h-5 w-5", "sm:h-7 sm:w-7")}`} /></span>
      </span>
    </button>
  );
};

const Player = ({ v }) => {
  if (v.kind === "upload" || v.kind === "file") {
    const src = v.kind === "upload" ? `${API}${v.src}` : v.src;
    return <video data-testid="video-tour-player" src={src} poster={v.poster ? `${API}${v.poster}` : undefined} controls autoPlay playsInline className="w-full aspect-video rounded-3xl bg-black" />;
  }
  return <iframe data-testid="video-tour-embed" src={v.embed} title={v.title || "HearthVault tour"} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" className="w-full aspect-video rounded-3xl bg-black border-0" />;
};

// Placeholder shown until the owner adds a video in Admin → "Landing-page tour video".
const Placeholder = () => (
  <div data-testid="video-tour-placeholder" className="relative w-full aspect-video rounded-3xl overflow-hidden border border-dashed border-border bg-card/60 grid place-items-center">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,hsl(var(--primary)/0.12),transparent_60%)]" />
    <div className="relative px-6 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 text-primary grid place-items-center"><Clapperboard className="h-5 w-5" /></div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] font-mono text-primary">{tr("Tour film in production")}</div>
      <div className="mt-1 font-serif text-xl sm:text-2xl">{tr("A 60-second walk through the vault lands here soon.")}</div>
      <ul className="mt-5 flex flex-wrap justify-center gap-2">
        {FRAMES.map((f, i) => <li key={f.label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground" style={{ animationDelay: `${i * 120}ms` }}><f.Icon className="h-3.5 w-3.5 text-primary" /> {tr(f.label)}</li>)}
      </ul>
    </div>
  </div>
);

export const VideoTour = () => {
  const { data } = useQuery({ queryKey: ["site-video"], queryFn: () => siteVideo("landing"), staleTime: 5 * 60_000, retry: false });
  const [playing, setPlaying] = useState(false);
  return (
    <section id="tour-video" className="max-w-6xl mx-auto px-6 pb-20" data-testid="video-tour-section">
      <motion.div {...fade()} className="grid lg:grid-cols-[0.8fr_1.4fr] gap-8 lg:gap-12 items-end">
        <div className="lg:pb-6">
          <Eyebrow>{tr("See it in action")}</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">{tr("One minute, start to finish.")}</h2>
          <p className="mt-3 text-muted-foreground">{tr("Drop in a receipt, a policy and a will. Watch them come back named, dated and filed — then see the reminder that fires before the policy lapses.")}</p>
        </div>
        <div>{data?.configured ? (playing ? <Player v={data} /> : <Facade title={data.title} poster={data.poster} onPlay={() => setPlaying(true)} />) : <Placeholder />}</div>
      </motion.div>
    </section>
  );
};
